import { ESdChangeReason, IDisplayItem, IScreenDesignDictionary } from "@kuika/kuika-cl-model";
import { PartialContextProvider } from "../../../shared/patial/partial-context-provider";
import { IScreenDesignContextState } from "../screen-design-context";
import { IScreenDesignContextProviderProps, ScreenDesignContextProvider } from "../screen-design-context-provider";

export interface DesignTimeElementStateProviderState {
  lockedElementIds: Set<number>;
  hiddenElementIds: Set<number>;
  toggleElementLocked: (elementId: number) => void;
  toggleElementHidden: (elementId: number) => void;
  isElementLocked: (elementId: number) => boolean;
  isElementHidden: (elementId: number) => boolean;
}

/**
 * `IDisplayItem.id` is typed `number`, but persisted screen JSON and some
 * creation flows (AI/MCP ui_render, legacy paste) produce string ids at
 * runtime. The lock/hide sets and the canvas receiver compare ids with
 * strict `Set` semantics (`Set<number>.has("643934")` is always false), so
 * every id entering this provider must be normalized to a number first.
 */
const toNumericElementId = (elementId: number | string): number => {
  return typeof elementId === "number" ? elementId : parseInt(elementId, 10);
};

export class DesignTimeElementStateProvider extends PartialContextProvider<
  ScreenDesignContextProvider,
  IScreenDesignContextProviderProps,
  IScreenDesignContextState,
  DesignTimeElementStateProviderState
> {
  static defaultState: DesignTimeElementStateProviderState = {
    lockedElementIds: new Set<number>(),
    hiddenElementIds: new Set<number>(),
    toggleElementLocked: () => {},
    toggleElementHidden: () => {},
    isElementLocked: () => false,
    isElementHidden: () => false
  };

  getProviderValues = (): DesignTimeElementStateProviderState => {
    return {
      lockedElementIds: this.state.lockedElementIds,
      hiddenElementIds: this.state.hiddenElementIds,
      toggleElementLocked: this.toggleElementLocked,
      toggleElementHidden: this.toggleElementHidden,
      isElementLocked: this.isElementLocked,
      isElementHidden: this.isElementHidden
    };
  };

  toggleElementLocked = (elementId: number): void => {
    const id: number = toNumericElementId(elementId);
    const next = new Set(this.state.lockedElementIds);
    const isNowLocked = !next.has(id);
    if (isNowLocked) {
      next.add(id);
    } else {
      next.delete(id);
    }

    const dict = (this.state as any).screenDesignDictionary;
    if (dict?.[id]) {
      dict[id].designTimeLocked = isNowLocked || undefined;
    }

    const sd = this.partial.screenProvider.getScreenDesign();
    this.partial.screenProvider.setScreenDesign(sd, ESdChangeReason.DesignTimeElementStateChanged);

    this.setState(
      {
        lockedElementIds: next,
        changeReason: ESdChangeReason.DesignTimeElementStateChanged
      } as any,
      () => {
        this.partial.frameProvider.renderFrame(ESdChangeReason.DesignTimeElementStateChanged);
      }
    );
  };

  toggleElementHidden = (elementId: number): void => {
    const id: number = toNumericElementId(elementId);
    const next = new Set(this.state.hiddenElementIds);
    const isNowHidden = !next.has(id);
    if (isNowHidden) {
      next.add(id);
    } else {
      next.delete(id);
    }

    const dict = (this.state as any).screenDesignDictionary;
    if (dict?.[id]) {
      dict[id].designTimeHidden = isNowHidden || undefined;
    }

    const sd = this.partial.screenProvider.getScreenDesign();
    this.partial.screenProvider.setScreenDesign(sd, ESdChangeReason.DesignTimeElementStateChanged);

    this.setState(
      {
        hiddenElementIds: next,
        changeReason: ESdChangeReason.DesignTimeElementStateChanged
      } as any,
      () => {
        this.partial.frameProvider.renderFrame(ESdChangeReason.DesignTimeElementStateChanged);
      }
    );
  };

  initializeFromScreenDesign = (): void => {
    const sd = this.partial.screenProvider.getScreenDesign();
    const lockedIds = new Set<number>();
    const hiddenIds = new Set<number>();

    if (sd?.displayItems?.length) {
      const queue: IDisplayItem[] = [...sd.displayItems];
      while (queue.length > 0) {
        const di = queue.shift()!;
        if (di.designTimeLocked) lockedIds.add(toNumericElementId(di.id));
        if (di.designTimeHidden) hiddenIds.add(toNumericElementId(di.id));
        if (di.children?.length) queue.push(...di.children);
      }
    }

    this.setState(
      {
        lockedElementIds: lockedIds,
        hiddenElementIds: hiddenIds,
        changeReason: ESdChangeReason.DesignTimeElementStateChanged
      } as any,
      () => {
        this.partial.frameProvider.renderFrame(ESdChangeReason.DesignTimeElementStateChanged);
      }
    );
  };

  isElementLocked = (elementId: number): boolean => {
    const id: number = toNumericElementId(elementId);
    if (this.state.lockedElementIds.has(id)) return true;
    const dict = (this.state as any).screenDesignDictionary;
    if (!dict) return false;
    let current = dict[id];
    while (current?.parentId != null) {
      if (this.state.lockedElementIds.has(toNumericElementId(current.parentId))) return true;
      current = dict[current.parentId];
    }
    return false;
  };

  isElementHidden = (elementId: number): boolean => {
    return this.state.hiddenElementIds.has(toNumericElementId(elementId));
  };

  /**
   * Drop lock/hide ids whose elements no longer exist in the tree. Deleting a
   * hidden/locked element (or a container holding hidden/locked descendants)
   * must clear those ids — otherwise the item-tree "filter" dropdown keeps a
   * stale, empty entry and the canvas keeps phantom state. Called on every
   * dictionary rebuild so all removal paths (canvas, tree, multi-select,
   * undo/redo) stay in sync without each having to clean up explicitly.
   */
  reconcileWithDictionary = (dict: IScreenDesignDictionary): void => {
    const prunedLocked: Set<number> = new Set<number>();
    const prunedHidden: Set<number> = new Set<number>();
    let changed: boolean = false;

    this.state.lockedElementIds.forEach((id: number): void => {
      if (dict[id]) prunedLocked.add(id);
      else changed = true;
    });
    this.state.hiddenElementIds.forEach((id: number): void => {
      if (dict[id]) prunedHidden.add(id);
      else changed = true;
    });

    if (!changed) return;

    this.setState({
      lockedElementIds: prunedLocked,
      hiddenElementIds: prunedHidden,
      changeReason: ESdChangeReason.DesignTimeElementStateChanged
    } as any);
  };
}
